import { useState, useCallback, useMemo } from "react";
import JSZip from "jszip";
import { splitPdf, parseSplitRange, getPdfPageCount } from "./operations";
import { pdfBytesToBlob } from "./utils";
import { FileDropZone, ProcessingState, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { StickyActionBar, STICKY_BAR_PRIMARY_CLASS } from "@/components/StickyActionBar";
import { wouldMark } from "@/lib/trialMark";
import { PROFESSIONAL } from "@/lib/tiers";
import { Link } from "wouter";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { usePdfPageThumbnails, PdfThumbnailLoading } from "@/components/PdfPageThumbnails";
import { useRunToken } from "@/hooks/useRunToken";
import { formatFileSize } from "@/lib/formatFileSize";
import { plural } from "@/lib/plural";

const TOOL_INFO_CONTENT = {
  // Subtitle/description clarified against a real, confirmed overlap
  // with /tool/extract-pdf: Split makes multiple new files (or one
  // reordered/reduced file) out of page ranges; Extract is for cherry-
  // picking specific pages OR pulling text content out. Someone who
  // wants a single page or the text itself is pointed there instead of
  // guessing which of the two tools is right from the name alone.
  what: `Breaks a single PDF into separate files by page range — extract just the pages you need, or list several ranges at once to get multiple files in one pass (packaged as one ZIP download when there's more than one file). Can also combine the selected ranges into a single reordered PDF instead, if that's what you actually need. Useful when you've received a large combined document but only need to share or file away a portion of it.\n\nUpload your PDF, check the page previews to confirm which pages are which, then type the page range or ranges you want (comma-separated, e.g. "1-3, 4-6, 7") — or click "Every page" for a one-click split into every individual page, no typing required. Choose whether you want each range as its own separate file, or all of them combined into one PDF in the order you listed them, then download.\n\nIf you only need one specific page pulled out, or you need the actual text content rather than a page-range PDF, Extract Pages & Text is the more direct tool for that — Split is built around ranges and multi-file output, not single-page or text extraction.`,
  tip: `Combining into one PDF is also a quick way to reorder or drop a few pages from a document — list the ranges in the order you want them to end up in.`,
};

interface ResultRow {
  blob: Blob;
  name: string;
  rangeLabel: string;
  pageCount: number;
  firstPage: number; // 0-indexed page of the source this part starts with, for its thumbnail
  formFields: number;
  bookmarks: number;
}

/** "Pages 2-3 appear in more than one file", or null when nothing repeats. */
function overlapNote(groups: number[][]): string | null {
  const seen = new Set<number>();
  const repeated = new Set<number>();
  for (const g of groups) for (const p of Array.from(new Set(g))) (seen.has(p) ? repeated : seen).add(p);
  if (repeated.size === 0) return null;
  const sorted = Array.from(repeated).sort((a, b) => a - b).map((p) => p + 1);
  const runs: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    runs.push(j > i ? `${sorted[i]}-${sorted[j]}` : `${sorted[i]}`);
    i = j + 1;
  }
  return `${repeated.size === 1 ? "Page" : "Pages"} ${runs.join(", ")} ${repeated.size === 1 ? "appears" : "appear"} in more than one file.`;
}

export default function SplitPdf() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [splitRange, setSplitRange] = useState("");
  const [combineIntoOne, setCombineIntoOne] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);
  const { thumbnails, loading } = usePdfPageThumbnails(file);
  const runs = useRunToken();
  const [everyN, setEveryN] = useState("");

  // Anything the person changes makes the files on screen stale, so they go away at once
  // (and a split still running for the old settings is dropped).
  const invalidate = () => {
    runs.cancel();
    setProcessing(false);
    setProgress(0);
    setResults([]);
    setNote(null);
  };

  const resetEverything = () => {
    invalidate();
    setFile(null);
    setPageCount(0);
    setSplitRange("");
    setCombineIntoOne(false);
    setEveryN("");
  };

  const handleEveryPage = () => {
    // One-click equivalent of typing "1,2,3,...,N" by hand.
    invalidate();
    setSplitRange(Array.from({ length: pageCount }, (_, i) => i + 1).join(","));
  };

  const handleEveryN = () => {
    const text = everyN.trim();
    const n = Number(text);
    if (!text || !Number.isInteger(n) || n < 1) {
      toast.error("Enter a whole number of 1 or more");
      return;
    }
    // Builds "1-N, N+1-2N, ..." -- e.g. every 3 pages on a 10-page document becomes
    // "1-3,4-6,7-9,10", each group downloading (or zipping) as its own file.
    invalidate();
    const ranges: string[] = [];
    for (let start = 1; start <= pageCount; start += n) {
      const end = Math.min(start + n - 1, pageCount);
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
    }
    setSplitRange(ranges.join(","));
  };

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    // A new file ends anything still running for the old one and starts from default settings.
    runs.cancel();
    setProcessing(false); setProgress(0); setResults([]); setNote(null);
    setCombineIntoOne(false); setEveryN("");
    try {
      const count = await getPdfPageCount(f);
      setFile(f);
      setPageCount(count);
      setSplitRange(count === 1 ? "1" : `1-${count}`);
    } catch {
      toast.error(`"${f.name}" couldn't be read as a PDF`);
    }
  }, [runs]);

  const handleStop = () => {
    runs.cancel();
    setProcessing(false);
    setProgress(0);
    toast.info("Stopped.");
  };

  const handleSplit = async () => {
    if (!file) return;
    // Whatever was on screen belongs to the previous settings, even if this attempt turns out invalid.
    invalidate();
    const ranges = splitRange.split(/[,;]/).map((r) => r.trim()).filter(Boolean);
    if (ranges.length === 0) { toast.error("Enter at least one page range"); return; }

    const parsed = ranges.map((r) => parseSplitRange(r, pageCount));
    const firstError = parsed.find((p): p is { error: string } => "error" in p);
    if (firstError) { toast.error(firstError.error); return; }

    const run = runs.start();
    setProcessing(true);
    try {
      const pageGroups = parsed.map((p) => (p as { pages: number[] }).pages);
      const output = await splitPdf(file, pageGroups, combineIntoOne, (current, total) => {
        if (runs.isCurrent(run)) setProgress((current / total) * 100);
      });
      if (!runs.isCurrent(run)) return; // stopped or changed while this was in flight -- discard silently
      setResults(output.map((r, i) => ({
        blob: pdfBytesToBlob(r.blob), name: r.name, rangeLabel: r.rangeLabel, pageCount: r.pageCount,
        firstPage: (combineIntoOne ? pageGroups[0] : pageGroups[i])[0] ?? 0, formFields: r.formFields, bookmarks: r.bookmarks,
      })));
      setNote(combineIntoOne ? null : overlapNote(pageGroups));
      if (combineIntoOne) {
        toast.success(`Combined ${plural(pageGroups.flat().length, "page")} into one PDF`);
      } else {
        toast.success(`Split into ${plural(output.length, "file")}`);
      }
    } catch (err) {
      if (!runs.isCurrent(run)) return;
      console.error("Split failed:", err);
      toast.error("Failed to split PDF");
    } finally {
      if (runs.isCurrent(run)) setProcessing(false);
    }
  };

  const handleDownloadZip = async () => {
    setZipping(true);
    try {
      const zip = new JSZip();
      for (const r of results) zip.file(r.name, r.blob);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      downloadBlob(zipBlob, `${(file?.name ?? "split").replace(/\.pdf$/i, "")}-split.zip`);
    } catch (err) {
      console.error("ZIP packaging failed:", err);
      toast.error("Couldn't package the files into a ZIP — download them individually below instead.");
    } finally {
      setZipping(false);
    }
  };

  const onePage = pageCount === 1;

  // Checked as the person types, so a bad range is explained next to the field and the button waits.
  const rangeError = useMemo(() => {
    if (!file) return null;
    const parts = splitRange.split(/[,;]/).map((r) => r.trim()).filter(Boolean);
    if (parts.length === 0) return "Enter at least one page range";
    for (const part of parts) {
      const r = parseSplitRange(part, pageCount);
      if ("error" in r) return r.error;
    }
    return null;
  }, [file, splitRange, pageCount]);

  // How many files the current ranges make (live, for the pinned bar). One when combining into a single PDF.
  const rangeParts = useMemo(() => splitRange.split(/[,;]/).map((r) => r.trim()).filter(Boolean), [splitRange]);
  const fileCount = combineIntoOne ? 1 : rangeParts.length;
  const barSummary = `${plural(pageCount, "page")} · ${plural(fileCount, "file")}${rangeParts.length > 0 && rangeParts.length <= 4 ? ` from ${rangeParts.join(", ")}` : ""}`;
  const markNoteVisible = results.length > 0 && results.some((r) => wouldMark(r.blob.type || "application/pdf", r.name, "split-pdf"));

  return (
    <ToolWithSidebar toolId="split-pdf" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      {!file ? (
        <FileDropZone gateAtDownload accept=".pdf" onFiles={handleFiles} label="Drop a PDF here, or click to browse" description="Split into multiple files by page ranges" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border border-border bg-card">
            <p className="text-sm font-medium">{file.name} — {plural(pageCount, "page")}</p>
            {onePage && <p className="text-xs text-muted-foreground mt-1">This PDF has only one page, so there is nothing to split.</p>}
          </div>

          {loading ? (
            <PdfThumbnailLoading />
          ) : (
            <div className="flex snap-x gap-2 overflow-x-auto rounded-lg sm:grid sm:grid-cols-4 sm:gap-3 sm:overflow-x-visible sm:overflow-y-auto sm:max-h-72 md:grid-cols-6" tabIndex={0} aria-label="Page previews">
              {thumbnails.map((thumb) => (
                <div key={thumb.pageNumber} className="flex w-[60px] shrink-0 snap-start flex-col items-center gap-1 sm:w-auto sm:shrink">
                  <div className="border rounded-lg overflow-hidden bg-white w-full" style={{ aspectRatio: "0.75" }}>
                    <img src={thumb.url} alt={`Page ${thumb.pageNumber}`} className="w-full h-full object-contain" />
                  </div>
                  <span className="text-xs text-muted-foreground">{thumb.pageNumber}</span>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="split-ranges">Page ranges (comma-separated, e.g. "1-3, 4-6, 7")</Label>
              <Button type="button" size="sm" variant="outline" className="max-sm:min-h-11" onClick={handleEveryPage} disabled={onePage}>
                Every page
              </Button>
            </div>
            <Input
              id="split-ranges"
              value={splitRange}
              onChange={(e) => { invalidate(); setSplitRange(e.target.value); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !processing && !onePage) { e.preventDefault(); void handleSplit(); } }}
              placeholder="1-3, 4-6"
              aria-invalid={rangeError ? true : undefined}
              aria-describedby={rangeError ? "split-ranges-error" : undefined}
              className="max-sm:h-11"
            />
            {rangeError && <p id="split-ranges-error" role="alert" className="text-sm text-destructive">{rangeError}</p>}
            <div className="flex items-center gap-2">
              <Label htmlFor="split-every-n" className="text-xs font-normal text-muted-foreground">Or split every</Label>
              <Input
                id="split-every-n"
                value={everyN}
                onChange={(e) => setEveryN(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleEveryN(); } }}
                placeholder="e.g. 3"
                aria-label="Split every this many pages"
                className="w-20 h-11 text-base sm:h-8 sm:text-sm"
                inputMode="numeric"
                disabled={onePage}
              />
              <span className="text-xs text-muted-foreground">pages</span>
              <Button type="button" size="sm" variant="outline" className="max-sm:min-h-11" onClick={handleEveryN} disabled={onePage}>
                Apply
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 max-sm:min-h-11">
            <input
              type="checkbox"
              id="combine-into-one"
              checked={combineIntoOne}
              onChange={(e) => { invalidate(); setCombineIntoOne(e.target.checked); }}
              className="h-4 w-4"
            />
            <Label htmlFor="combine-into-one" className="cursor-pointer font-normal">
              Combine all ranges into one PDF file, instead of separate files
            </Label>
          </div>
          <div className="flex gap-3">
            {processing ? (
              <ProcessingState message={combineIntoOne ? "Combining..." : "Splitting..."} progress={combineIntoOne ? undefined : progress} onStop={handleStop} />
            ) : (
              <>
                <Button onClick={handleSplit} disabled={onePage || !!rangeError} className="btn-glow max-sm:hidden">Split PDF</Button>
                <Button variant="outline" className="max-sm:min-h-11" onClick={resetEverything}>Reset</Button>
                {/* Phones: the Split action stays pinned (above the anchor ad) with a live "N files from ranges" summary;
                    once split it becomes Download all (ZIP), or the single file's Download. */}
                {results.length === 0 && (
                  <StickyActionBar>
                    <div className="min-w-0 flex-1 text-[13px] leading-tight text-muted-foreground" data-testid="split-bar-summary">{barSummary}</div>
                    <Button onClick={handleSplit} disabled={onePage || !!rangeError} className={`${STICKY_BAR_PRIMARY_CLASS} btn-glow`} data-testid="split-bar-button">
                      {combineIntoOne ? "Combine into 1 file" : `Split into ${plural(fileCount, "file")}`}
                    </Button>
                  </StickyActionBar>
                )}
                {results.length > 1 && (
                  <StickyActionBar>
                    <div className="min-w-0 flex-1 text-[13px] leading-tight text-muted-foreground">{plural(results.length, "file")} ready</div>
                    <Button onClick={handleDownloadZip} disabled={zipping} className={STICKY_BAR_PRIMARY_CLASS} data-testid="split-bar-zip">
                      {zipping ? "Packaging..." : "Download all (ZIP)"}
                    </Button>
                  </StickyActionBar>
                )}
              </>
            )}
          </div>
          {results.length > 0 && (
            <div className="space-y-2 pt-4 border-t">
              {note && <p className="text-xs text-muted-foreground">{note}</p>}
              {/* Phones: the badge note once, here, instead of under every file (rows hide theirs below 640px). */}
              {markNoteVisible && (
                <p className="text-xs text-muted-foreground sm:hidden" data-testid="split-mark-note-once">
                  Trial downloads carry a small badge. Remove it with the {PROFESSIONAL.dayPass} day pass.{" "}
                  <Link href="/pricing" className="inline-flex min-h-11 items-center underline">See passes</Link>
                </p>
              )}
              {results.length > 1 && (
                <Button onClick={handleDownloadZip} disabled={zipping} className="w-full btn-glow max-sm:hidden">
                  {zipping ? "Packaging ZIP..." : `Download all ${results.length} files as ZIP`}
                </Button>
              )}
              {results.map((r, i) => {
                const thumb = thumbnails[r.firstPage];
                return (
                  <div key={`${r.name}-${i}`} className="split-result flex items-center justify-between gap-3 p-3 rounded-lg border">
                    <div className="flex items-center gap-3 min-w-0">
                      {thumb && (
                        <div className="w-10 shrink-0 border rounded overflow-hidden bg-white" style={{ aspectRatio: "0.75" }}>
                          <img src={thumb.url} alt={`First page of ${r.name}`} className="w-full h-full object-contain" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm truncate max-sm:overflow-visible max-sm:whitespace-normal max-sm:[overflow-wrap:anywhere]" title={r.name}>{r.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.rangeLabel} · {plural(r.pageCount, "page")} · {formatFileSize(r.blob.size)}
                          {r.formFields > 0 && ` · ${plural(r.formFields, "form field")} kept`}
                          {r.bookmarks > 0 && ` · ${plural(r.bookmarks, "bookmark")} kept`}
                        </p>
                      </div>
                    </div>
                    <DownloadButton blob={r.blob} fileName={r.name} label="Download" hideMarkNoticeOnPhone pinned={results.length === 1 ? { summary: `${r.rangeLabel} · ${plural(r.pageCount, "page")} · ${formatFileSize(r.blob.size)}` } : undefined} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </ToolWithSidebar>
  );
}
